import {
  fillMissingTimestamps,
  filterInvoicesByCommunityPeriod,
  filterInvoicesByMemberPeriod,
  formatDate,
  getTimeGranularity,
  getTimeGranularityOverview,
  httpService,
} from '@flexo/general';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { normalizeInvoicesResponse } from './invoices.utils';

const ENV = (import.meta as any).env;
const LOCALE =
  navigator.language.split('-')[0] ||
  ENV?.VITE_APP_LOCALE.split('-')[0] ||
  'en';
const MIN_TIME_RESOLUTION = ENV?.VITE_APP_ORG === 'kwick' ? '15min' : null;

// Helpers for member KPI requests: choose site/{id} vs site/.../grouped and build params
type KpiKind = 'energy' | 'ancillary' | 'economics';

const okOrNull = (res: any) => (res?.status === 200 ? res.data : null);
const swallowErr = () => null;

function buildOverviewParams(selectedDate: any, selectedTimeSet: any) {
  return {
    dateFrom: formatDate(selectedDate[0]),
    dateTo: formatDate(selectedDate[1]),
    timeGranularity: getTimeGranularityOverview(selectedTimeSet),
    locale: LOCALE,
  };
}

function buildDetailParams(
  selectedDate: any,
  selectedTimeSet: any,
  minRes: string | null
) {
  return {
    dateFrom: formatDate(selectedDate[0]),
    dateTo: formatDate(selectedDate[1]),
    timeGranularity: getTimeGranularity(selectedTimeSet, minRes),
    locale: LOCALE,
  };
}

function buildMemberOverviewDetailRequests(
  kind: KpiKind,
  args: { sites: string[]; selectedDate: any; selectedTimeSet: any }
) {
  const { sites, selectedDate, selectedTimeSet } = args;
  const justOneSite = sites?.length === 1;
  const detailMin = kind === 'energy' ? MIN_TIME_RESOLUTION : null;

  const overview = buildOverviewParams(selectedDate, selectedTimeSet);
  const detail = buildDetailParams(selectedDate, selectedTimeSet, detailMin);

  if (justOneSite) {
    const siteId = sites[0];
    return [
      httpService
        .get({
          url: `${ENV.VITE_APP_BASE_URL}/community/kpi/v2_0/site/${kind}/${siteId}`,
          params: overview,
        })
        .then(okOrNull)
        .catch(swallowErr),
      httpService
        .get({
          url: `${ENV.VITE_APP_BASE_URL}/community/kpi/v2_0/site/${kind}/${siteId}`,
          params: detail,
        })
        .then(okOrNull)
        .catch(swallowErr),
    ];
  }

  return [
    httpService
      .post({
        url: `${ENV.VITE_APP_BASE_URL}/community/kpi/v2_0/site/${kind}/grouped`,
        data: { ...overview, siteId: sites },
      })
      .then(okOrNull)
      .catch(swallowErr),
    httpService
      .post({
        url: `${ENV.VITE_APP_BASE_URL}/community/kpi/v2_0/site/${kind}/grouped`,
        data: { ...detail, siteId: sites },
      })
      .then(okOrNull)
      .catch(swallowErr),
  ];
}

function buildMemberCustomRequest(
  kind: Extract<KpiKind, 'energy' | 'economics'>,
  args: {
    sites: string[];
    selectedDate: any;
    selectedTimeSet: any;
    includeLocaleOnGrouped?: boolean;
  }
) {
  const {
    sites,
    selectedDate,
    selectedTimeSet,
    includeLocaleOnGrouped = true,
  } = args;
  const justOneSite = sites?.length === 1;

  const baseCustomSiteParams = {
    dateFrom: formatDate(selectedDate[0]),
    dateTo: formatDate(selectedDate[1]),
    timeGranularity: getTimeGranularityOverview(selectedTimeSet),
    aggregationLevel: 'site',
    locale: LOCALE,
  };

  const baseGroupedData: any = {
    dateFrom: formatDate(selectedDate[0]),
    dateTo: formatDate(selectedDate[1]),
    siteId: sites,
    timeGranularity: getTimeGranularityOverview(selectedTimeSet),
    aggregationLevel: 'member',
  };
  if (includeLocaleOnGrouped) baseGroupedData.locale = LOCALE;

  if (justOneSite) {
    const siteId = sites[0];
    return httpService
      .get({
        url: `${ENV.VITE_APP_BASE_URL}/community/kpi/v2_0/customKpi/${kind}/${siteId}`,
        params: baseCustomSiteParams,
      })
      .then(okOrNull)
      .catch(swallowErr);
  }

  return httpService
    .post({
      url: `${ENV.VITE_APP_BASE_URL}/community/kpi/v2_0/site/custom/${kind}/grouped`,
      data: baseGroupedData,
    })
    .then(okOrNull)
    .catch(swallowErr);
}

export const SetComparisonKpiDataCommunityAsync = createAsyncThunk(
  'kpi/SetComparisonKpiDataCommunityAsync',
  async function (data: any) {
    const { COMMUNITY_ID, selectedDate, selectedTimeSet, siteIds } = data;

    const [kpiComparisonResponse] = await Promise.all([
      httpService
        .post({
          url: `${ENV.VITE_APP_BASE_URL}/community/kpi/v2_0/siteCommunity/comparison/${COMMUNITY_ID}`,
          data: {
            dateFrom: formatDate(selectedDate[0]),
            dateTo: formatDate(selectedDate[1]),
            timeGranularity: getTimeGranularityOverview(selectedTimeSet),
            locale: LOCALE,
            siteId: siteIds,
          },
        })
        .then((res: any) => {
          if (res.status === 200) {
            return res.data;
          } else {
            // console.warn({
            //   status: res.status,
            //   message: res.statusText,
            // });
            return null;
          }
        })
        .catch((err: any) => {
          // setAlertStore({
          //   type: AlertTypes.SetAlert,
          //   payload: {
          //     value: {
          //       type: 'BANNER_UNEXPECTED_ERROR',
          //     },
          //   },
          // });

          return null;
        }),
    ]);

    return {
      comparison: kpiComparisonResponse,
    };
  }
);

export const SetEnergyKpiDataCommunityAsync = createAsyncThunk(
  'kpi/SetEnergyKpiDataCommunityAsync',
  async function (data: any) {
    const { COMMUNITY_ID, selectedDate, selectedTimeSet, metadata } = data;

    const [kpiCommunityOverview, kpiCommunityDetail] = await Promise.all([
      httpService
        .get({
          url: `${ENV.VITE_APP_BASE_URL}/community/kpi/v2_0/community/energy/${COMMUNITY_ID}`,
          params: {
            dateFrom: formatDate(selectedDate[0]),
            dateTo: formatDate(selectedDate[1]),
            //add null: null to test the error
            timeGranularity: getTimeGranularityOverview(selectedTimeSet),
            locale: LOCALE,
          },
          // ${ENV.VITE_APP_BASE_URL}/community/kpi/v1_2/community/${metaDataApiStore.selectedEntityId}
        })
        .then((res: any) => {
          if (res.status === 200) {
            return res.data;
          } else {
            return null;
          }
        })
        .catch((err: any) => {
          // console.warn(err);

          // setAlertStore({
          //   type: AlertTypes.SetAlert,
          //   payload: {
          //     value: {
          //       type: 'BANNER_UNEXPECTED_ERROR',
          //     },
          //   },
          // });

          return null;
        }),

      // New endpoint request
      httpService
        .get({
          url: `${ENV.VITE_APP_BASE_URL}/community/kpi/v2_0/community/energy/${COMMUNITY_ID}`,
          params: {
            dateFrom: formatDate(selectedDate[0]),
            dateTo: formatDate(selectedDate[1]),
            timeGranularity: getTimeGranularity(
              selectedTimeSet,
              MIN_TIME_RESOLUTION
            ),
            locale: LOCALE,
          },
        })
        .then((res: any) => {
          if (res.status === 200) {
            return res.data;
          } else {
            // console.warn({
            //   status: res.status,
            //   message: res.statusText,
            // });
            return null;
          }
        })
        .catch((err: any) => {
          // setAlertStore({
          //   type: AlertTypes.SetAlert,
          //   payload: {
          //     value: {
          //       type: 'BANNER_UNEXPECTED_ERROR',
          //     },
          //   },
          // });

          return null;
        }),
    ]);

    // Fill missing timestamps for energy data
    const filledOverview = fillMissingTimestamps(
      kpiCommunityOverview,
      formatDate(selectedDate[0]),
      formatDate(selectedDate[1]),
      getTimeGranularityOverview(selectedTimeSet) as any
    );

    const filledDetail = fillMissingTimestamps(
      kpiCommunityDetail,
      formatDate(selectedDate[0]),
      formatDate(selectedDate[1]),
      getTimeGranularity(selectedTimeSet, MIN_TIME_RESOLUTION) as any
    );

    return {
      '1day': filledOverview,
      '1hour': filledDetail,
    };
  }
);

export const SetEnergyCustomKpiDataCommunityAsync = createAsyncThunk(
  'kpi/SetEnergyCustomKpiDataCommunityAsync',
  async function (data: any) {
    const { COMMUNITY_ID, selectedDate, selectedTimeSet } = data;
    const [customKpiCommunityData] = await Promise.all([
      httpService
        .get({
          url: `${ENV.VITE_APP_BASE_URL}/community/kpi/v2_0/customKpi/energy/${COMMUNITY_ID}`,
          params: {
            dateFrom: formatDate(selectedDate[0]),
            dateTo: formatDate(selectedDate[1]),
            //add null: null to test the error
            timeGranularity: getTimeGranularityOverview(selectedTimeSet),
            aggregationLevel: 'community',
            locale: LOCALE,
          },
        })
        .then((res: any) => {
          if (res.status === 200) {
            return res.data;
          } else {
            // console.warn({
            //   status: res.status,
            //   message: res.statusText,
            // });
            return null;
          }
        })
        .catch((err: any) => {
          // console.warn(err);

          // setAlertStore({
          //   type: AlertTypes.SetAlert,
          //   payload: {
          //     value: {
          //       type: 'BANNER_UNEXPECTED_ERROR',
          //     },
          //   },
          // });

          return null;
        }),
    ]);

    return customKpiCommunityData;
  }
);

export const SetEconomicsCustomKpiDataCommunityAsync = createAsyncThunk(
  'kpi/SetEconomicsCustomKpiDataCommunityAsync',
  async function (data: any) {
    const { COMMUNITY_ID, selectedDate, selectedTimeSet } = data;
    const [customKpiCommunityData] = await Promise.all([
      httpService
        .get({
          url: `${ENV.VITE_APP_BASE_URL}/community/kpi/v2_0/customKpi/economics/${COMMUNITY_ID}`,
          params: {
            dateFrom: formatDate(selectedDate[0]),
            dateTo: formatDate(selectedDate[1]),
            //add null: null to test the error
            timeGranularity: getTimeGranularityOverview(selectedTimeSet),
            aggregationLevel: 'community',
            locale: LOCALE,
          },
        })
        .then((res: any) => {
          if (res.status === 200) {
            return res.data;
          } else {
            // console.warn({
            //   status: res.status,
            //   message: res.statusText,
            // });
            return null;
          }
        })
        .catch((err: any) => {
          // console.warn(err);

          // setAlertStore({
          //   type: AlertTypes.SetAlert,
          //   payload: {
          //     value: {
          //       type: 'BANNER_UNEXPECTED_ERROR',
          //     },
          //   },
          // });

          return null;
        }),
    ]);

    // Fill missing timestamps for economics custom KPI data
    const filledCustomKpiCommunityData = fillMissingTimestamps(
      customKpiCommunityData,
      formatDate(selectedDate[0]),
      formatDate(selectedDate[1]),
      getTimeGranularityOverview(selectedTimeSet) as any
    );

    return filledCustomKpiCommunityData;
  }
);

export const SetEconomicsKpiDataCommunityAsync = createAsyncThunk(
  'kpi/SetEconomicsKpiDataCommunityAsync',
  async function (data: any) {
    const { COMMUNITY_ID, selectedDate, selectedTimeSet, metadata } = data;

    const [kpiCommunityOverview, kpiCommunityDetail] = await Promise.all([
      httpService
        .get({
          url: `${ENV.VITE_APP_BASE_URL}/community/kpi/v2_0/community/economics/${COMMUNITY_ID}`,
          params: {
            dateFrom: formatDate(selectedDate[0]),
            dateTo: formatDate(selectedDate[1]),
            //add null: null to test the error
            timeGranularity: getTimeGranularityOverview(selectedTimeSet),
            locale: LOCALE,
          },
          // ${ENV.VITE_APP_BASE_URL}/community/kpi/v1_2/community/${metaDataApiStore.selectedEntityId}
        })
        .then((res: any) => {
          if (res.status === 200) {
            return res.data;
          } else {
            return null;
          }
        })
        .catch((err: any) => {
          // console.warn(err);

          // setAlertStore({
          //   type: AlertTypes.SetAlert,
          //   payload: {
          //     value: {
          //       type: 'BANNER_UNEXPECTED_ERROR',
          //     },
          //   },
          // });

          return null;
        }),

      // New endpoint request
      httpService
        .get({
          url: `${ENV.VITE_APP_BASE_URL}/community/kpi/v2_0/community/economics/${COMMUNITY_ID}`,
          params: {
            dateFrom: formatDate(selectedDate[0]),
            dateTo: formatDate(selectedDate[1]),
            timeGranularity: getTimeGranularity(
              selectedTimeSet,
              MIN_TIME_RESOLUTION
            ),
            locale: LOCALE,
          },
        })
        .then((res: any) => {
          if (res.status === 200) {
            return res.data;
          } else {
            // console.warn({
            //   status: res.status,
            //   message: res.statusText,
            // });
            return null;
          }
        })
        .catch((err: any) => {
          // setAlertStore({
          //   type: AlertTypes.SetAlert,
          //   payload: {
          //     value: {
          //       type: 'BANNER_UNEXPECTED_ERROR',
          //     },
          //   },
          // });

          return null;
        }),
    ]);

    // Fill missing timestamps for economics data
    const filledOverview = fillMissingTimestamps(
      kpiCommunityOverview,
      formatDate(selectedDate[0]),
      formatDate(selectedDate[1]),
      getTimeGranularityOverview(selectedTimeSet) as any
    );

    const filledDetail = fillMissingTimestamps(
      kpiCommunityDetail,
      formatDate(selectedDate[0]),
      formatDate(selectedDate[1]),
      getTimeGranularity(selectedTimeSet, MIN_TIME_RESOLUTION) as any
    );

    return {
      '1day': filledOverview,
      '1hour': filledDetail,
      isLoading: false,
    };
  }
);

export const SetAncillaryKpiDataCommunityAsync = createAsyncThunk(
  'kpi/SetAncillaryKpiDataCommunityAsync',
  async function (data: any) {
    const { COMMUNITY_ID, selectedDate, selectedTimeSet, metadata } = data;

    const [kpiCommunityOverview, kpiCommunityDetail] = await Promise.all([
      httpService
        .get({
          url: `${ENV.VITE_APP_BASE_URL}/community/kpi/v2_0/community/ancillary/${COMMUNITY_ID}`,
          params: {
            dateFrom: formatDate(selectedDate[0]),
            dateTo: formatDate(selectedDate[1]),
            //add null: null to test the error
            timeGranularity: getTimeGranularityOverview(selectedTimeSet),
            locale: LOCALE,
          },
          // ${ENV.VITE_APP_BASE_URL}/community/kpi/v1_2/community/${metaDataApiStore.selectedEntityId}
        })
        .then((res: any) => {
          if (res.status === 200) {
            return res.data;
          } else {
            return null;
          }
        })
        .catch((err: any) => {
          // console.warn(err);

          // setAlertStore({
          //   type: AlertTypes.SetAlert,
          //   payload: {
          //     value: {
          //       type: 'BANNER_UNEXPECTED_ERROR',
          //     },
          //   },
          // });

          return null;
        }),

      // New endpoint request
      httpService
        .get({
          url: `${ENV.VITE_APP_BASE_URL}/community/kpi/v2_0/community/ancillary/${COMMUNITY_ID}`,
          params: {
            dateFrom: formatDate(selectedDate[0]),
            dateTo: formatDate(selectedDate[1]),
            timeGranularity: getTimeGranularity(
              selectedTimeSet,
              MIN_TIME_RESOLUTION
            ),
            locale: LOCALE,
          },
        })
        .then((res: any) => {
          if (res.status === 200) {
            return res.data;
          } else {
            // console.warn({
            //   status: res.status,
            //   message: res.statusText,
            // });
            return null;
          }
        })
        .catch((err: any) => {
          // setAlertStore({
          //   type: AlertTypes.SetAlert,
          //   payload: {
          //     value: {
          //       type: 'BANNER_UNEXPECTED_ERROR',
          //     },
          //   },
          // });

          return null;
        }),
    ]);

    return {
      '1day': kpiCommunityOverview,
      '1hour': kpiCommunityDetail,
    };
  }
);

export const SetEnergyKpiDataSiteAsync = createAsyncThunk(
  'kpi/SetEnergyKpiDataSiteAsync',
  async function (data: any) {
    const { SITE_ID, selectedDate, selectedTimeSet } = data;

    const [kpiSiteDataOverview, kpiSiteDataDetail] = await Promise.all([
      httpService
        .get({
          url: `${ENV.VITE_APP_BASE_URL}/community/kpi/v2_0/site/energy/${SITE_ID}`,
          params: {
            dateFrom: formatDate(selectedDate[0]),
            dateTo: formatDate(selectedDate[1]),
            //add null: null to test the error
            timeGranularity: getTimeGranularityOverview(selectedTimeSet),
            locale: LOCALE,
          },
        })
        .then((res: any) => {
          if (res.status === 200) {
            return res.data;
          } else {
            // console.warn({
            //   status: res.status,
            //   message: res.statusText,
            // });
            return null;
          }
        })
        .catch((err: any) => {
          // console.warn(err);

          // setAlertStore({
          //   type: AlertTypes.SetAlert,
          //   payload: {
          //     value: {
          //       type: 'BANNER_UNEXPECTED_ERROR',
          //     },
          //   },
          // });

          return null;
        }),

      httpService
        .get({
          url: `${ENV.VITE_APP_BASE_URL}/community/kpi/v2_0/site/energy/${SITE_ID}`,
          params: {
            dateFrom: formatDate(selectedDate[0]),
            dateTo: formatDate(selectedDate[1]),
            timeGranularity: getTimeGranularity(
              selectedTimeSet,
              MIN_TIME_RESOLUTION
            ),
            locale: LOCALE,
          },
        })
        .then((res: any) => {
          if (res.status === 200) {
            return res.data;
          } else {
            // console.warn({
            //   status: res.status,
            //   message: res.statusText,
            // });
            return null;
          }
        })
        .catch((err: any) => {
          // setAlertStore({
          //   type: AlertTypes.SetAlert,
          //   payload: {
          //     value: {
          //       type: 'BANNER_UNEXPECTED_ERROR',
          //     },
          //   },
          // });

          return null;
        }),
    ]);

    // Fill missing timestamps for energy data
    const filledOverview = fillMissingTimestamps(
      kpiSiteDataOverview,
      formatDate(selectedDate[0]),
      formatDate(selectedDate[1]),
      getTimeGranularityOverview(selectedTimeSet) as any
    );

    const filledDetail = fillMissingTimestamps(
      kpiSiteDataDetail,
      formatDate(selectedDate[0]),
      formatDate(selectedDate[1]),
      getTimeGranularity(selectedTimeSet, MIN_TIME_RESOLUTION) as any
    );

    return {
      '1day': filledOverview,
      '1hour': filledDetail,
    };
  }
);

export const SetAncillaryKpiDataSiteAsync = createAsyncThunk(
  'kpi/SetAncillaryKpiDataSiteAsync',
  async function (data: any) {
    const { SITE_ID, selectedDate, selectedTimeSet } = data;

    const [kpiSiteDataOverview, kpiSiteDataDetail] = await Promise.all([
      httpService
        .get({
          url: `${ENV.VITE_APP_BASE_URL}/community/kpi/v2_0/site/ancillary/${SITE_ID}`,
          params: {
            dateFrom: formatDate(selectedDate[0]),
            dateTo: formatDate(selectedDate[1]),
            //add null: null to test the error
            timeGranularity: getTimeGranularityOverview(selectedTimeSet),
            locale: LOCALE,
          },
        })
        .then((res: any) => {
          if (res.status === 200) {
            return res.data;
          } else {
            // console.warn({
            //   status: res.status,
            //   message: res.statusText,
            // });
            return null;
          }
        })
        .catch((err: any) => {
          // console.warn(err);

          // setAlertStore({
          //   type: AlertTypes.SetAlert,
          //   payload: {
          //     value: {
          //       type: 'BANNER_UNEXPECTED_ERROR',
          //     },
          //   },
          // });

          return null;
        }),

      httpService
        .get({
          url: `${ENV.VITE_APP_BASE_URL}/community/kpi/v2_0/site/ancillary/${SITE_ID}`,
          params: {
            dateFrom: formatDate(selectedDate[0]),
            dateTo: formatDate(selectedDate[1]),
            timeGranularity: getTimeGranularity(
              selectedTimeSet,
              MIN_TIME_RESOLUTION
            ),
            locale: LOCALE,
          },
        })
        .then((res: any) => {
          if (res.status === 200) {
            return res.data;
          } else {
            // console.warn({
            //   status: res.status,
            //   message: res.statusText,
            // });
            return null;
          }
        })
        .catch((err: any) => {
          // setAlertStore({
          //   type: AlertTypes.SetAlert,
          //   payload: {
          //     value: {
          //       type: 'BANNER_UNEXPECTED_ERROR',
          //     },
          //   },
          // });

          return null;
        }),
    ]);

    return {
      '1day': kpiSiteDataOverview,
      '1hour': kpiSiteDataDetail,
    };
  }
);

export const SetEconomicsKpiDataSiteAsync = createAsyncThunk(
  'kpi/SetEconomicsKpiDataSiteAsync',
  async function (data: any) {
    const { SITE_ID, selectedDate, selectedTimeSet } = data;

    const [kpiSiteDataOverview, kpiSiteDataDetail] = await Promise.all([
      httpService
        .get({
          url: `${ENV.VITE_APP_BASE_URL}/community/kpi/v2_0/site/economics/${SITE_ID}`,
          params: {
            dateFrom: formatDate(selectedDate[0]),
            dateTo: formatDate(selectedDate[1]),
            //add null: null to test the error
            timeGranularity: getTimeGranularityOverview(selectedTimeSet),
            locale: LOCALE,
          },
        })
        .then((res: any) => {
          if (res.status === 200) {
            return res.data;
          } else {
            // console.warn({
            //   status: res.status,
            //   message: res.statusText,
            // });
            return null;
          }
        })
        .catch((err: any) => {
          // console.warn(err);

          // setAlertStore({
          //   type: AlertTypes.SetAlert,
          //   payload: {
          //     value: {
          //       type: 'BANNER_UNEXPECTED_ERROR',
          //     },
          //   },
          // });

          return null;
        }),

      httpService
        .get({
          url: `${ENV.VITE_APP_BASE_URL}/community/kpi/v2_0/site/economics/${SITE_ID}`,
          params: {
            dateFrom: formatDate(selectedDate[0]),
            dateTo: formatDate(selectedDate[1]),
            timeGranularity: getTimeGranularity(
              selectedTimeSet,
              MIN_TIME_RESOLUTION
            ),
            locale: LOCALE,
          },
        })
        .then((res: any) => {
          if (res.status === 200) {
            return res.data;
          } else {
            // console.warn({
            //   status: res.status,
            //   message: res.statusText,
            // });
            return null;
          }
        })
        .catch((err: any) => {
          // setAlertStore({
          //   type: AlertTypes.SetAlert,
          //   payload: {
          //     value: {
          //       type: 'BANNER_UNEXPECTED_ERROR',
          //     },
          //   },
          // });

          return null;
        }),
    ]);

    // Fill missing timestamps for economics data
    const filledOverview = fillMissingTimestamps(
      kpiSiteDataOverview,
      formatDate(selectedDate[0]),
      formatDate(selectedDate[1]),
      getTimeGranularityOverview(selectedTimeSet) as any
    );

    const filledDetail = fillMissingTimestamps(
      kpiSiteDataDetail,
      formatDate(selectedDate[0]),
      formatDate(selectedDate[1]),
      getTimeGranularity(selectedTimeSet, MIN_TIME_RESOLUTION) as any
    );

    return {
      '1day': filledOverview,
      '1hour': filledDetail,
    };
  }
);

export const SetEconomicsCustomKpiDataSiteAsync = createAsyncThunk(
  'kpi/SetEconomicsCustomKpiDataSiteAsync',
  async function (data: any) {
    const { SITE_ID, selectedDate, selectedTimeSet } = data;
    const [customKpiSiteData] = await Promise.all([
      httpService
        .get({
          url: `${ENV.VITE_APP_BASE_URL}/community/kpi/v2_0/customKpi/economics/${SITE_ID}`,
          params: {
            dateFrom: formatDate(selectedDate[0]),
            dateTo: formatDate(selectedDate[1]),
            //add null: null to test the error
            timeGranularity: getTimeGranularityOverview(selectedTimeSet),
            aggregationLevel: 'site',
            locale: LOCALE,
          },
        })
        .then((res: any) => {
          if (res.status === 200) {
            return res.data;
          } else {
            // console.warn({
            //   status: res.status,
            //   message: res.statusText,
            // });
            return null;
          }
        })
        .catch((err: any) => {
          // console.warn(err);

          // setAlertStore({
          //   type: AlertTypes.SetAlert,
          //   payload: {
          //     value: {
          //       type: 'BANNER_UNEXPECTED_ERROR',
          //     },
          //   },
          // });

          return null;
        }),
    ]);

    // Fill missing timestamps for economics custom KPI data
    const filledCustomKpiSiteData = fillMissingTimestamps(
      customKpiSiteData,
      formatDate(selectedDate[0]),
      formatDate(selectedDate[1]),
      getTimeGranularityOverview(selectedTimeSet) as any
    );

    return filledCustomKpiSiteData;
  }
);

export const SetEnergyKpiDataMemberAsync = createAsyncThunk(
  'kpi/SetEnergyKpiDataMemberAsync',
  async function (data: any) {
    const { sites, selectedDate, selectedTimeSet } = data;

    const [kpiMemberDataOverview, kpiMemberDataDetail] = await Promise.all(
      buildMemberOverviewDetailRequests('energy', {
        sites,
        selectedDate,
        selectedTimeSet,
      })
    );

    // Fill missing timestamps for energy data
    const filledOverview = fillMissingTimestamps(
      kpiMemberDataOverview,
      formatDate(selectedDate[0]),
      formatDate(selectedDate[1]),
      getTimeGranularityOverview(selectedTimeSet) as any
    );

    const filledDetail = fillMissingTimestamps(
      kpiMemberDataDetail,
      formatDate(selectedDate[0]),
      formatDate(selectedDate[1]),
      getTimeGranularity(selectedTimeSet, MIN_TIME_RESOLUTION) as any
    );

    return {
      '1day': filledOverview,
      '1hour': filledDetail,
    };
  }
);

export const SetEnergyCustomKpiDataSiteAsync = createAsyncThunk(
  'kpi/SetEnergyCustomKpiDataSiteAsync',
  async function (data: any) {
    const { SITE_ID, selectedDate, selectedTimeSet } = data;
    const [customKpiSiteData] = await Promise.all([
      httpService
        .get({
          url: `${ENV.VITE_APP_BASE_URL}/community/kpi/v2_0/customKpi/energy/${SITE_ID}`,
          params: {
            dateFrom: formatDate(selectedDate[0]),
            dateTo: formatDate(selectedDate[1]),
            //add null: null to test the error
            timeGranularity: getTimeGranularityOverview(selectedTimeSet),
            aggregationLevel: 'site',
            locale: LOCALE,
          },
        })
        .then((res: any) => {
          if (res.status === 200) {
            return res.data;
          } else {
            // console.warn({
            //   status: res.status,
            //   message: res.statusText,
            // });
            return null;
          }
        })
        .catch((err: any) => {
          // console.warn(err);

          // setAlertStore({
          //   type: AlertTypes.SetAlert,
          //   payload: {
          //     value: {
          //       type: 'BANNER_UNEXPECTED_ERROR',
          //     },
          //   },
          // });

          return null;
        }),
    ]);

    return customKpiSiteData;
  }
);

export const SetEnergyCustomKpiDataMemberAsync = createAsyncThunk(
  'kpi/SetEnergyCustomKpiDataMemberAsync',
  async function (data: any) {
    const { sites, selectedDate, selectedTimeSet } = data;
    const customKpiMemberData = await buildMemberCustomRequest('energy', {
      sites,
      selectedDate,
      selectedTimeSet,
      includeLocaleOnGrouped: true,
    });

    return customKpiMemberData;
  }
);

export const SetAncillaryKpiDataMemberAsync = createAsyncThunk(
  'kpi/SetAncillaryKpiDataMemberAsync',
  async function (data: any) {
    const { sites, selectedDate, selectedTimeSet } = data;
    const [kpiMemberDataOverview, kpiMemberDataDetail] = await Promise.all(
      buildMemberOverviewDetailRequests('ancillary', {
        sites,
        selectedDate,
        selectedTimeSet,
      })
    );

    return {
      '1day': kpiMemberDataOverview,
      '1hour': kpiMemberDataDetail,
    };
  }
);

export const SetEconomicsKpiDataMemberAsync = createAsyncThunk(
  'kpi/SetEconomicsKpiDataMemberAsync',
  async function (data: any) {
    const { sites, selectedDate, selectedTimeSet } = data;
    const [kpiMemberDataOverview, kpiMemberDataDetail] = await Promise.all(
      buildMemberOverviewDetailRequests('economics', {
        sites,
        selectedDate,
        selectedTimeSet,
      })
    );

    // Fill missing timestamps for economics data
    const filledOverview = fillMissingTimestamps(
      kpiMemberDataOverview,
      formatDate(selectedDate[0]),
      formatDate(selectedDate[1]),
      getTimeGranularityOverview(selectedTimeSet) as any
    );

    const filledDetail = fillMissingTimestamps(
      kpiMemberDataDetail,
      formatDate(selectedDate[0]),
      formatDate(selectedDate[1]),
      getTimeGranularity(selectedTimeSet, MIN_TIME_RESOLUTION) as any
    );

    return {
      '1day': filledOverview,
      '1hour': filledDetail,
    };
  }
);

export const SetEconomicsCustomKpiDataMemberAsync = createAsyncThunk(
  'kpi/SetEconomicsCustomKpiDataMemberAsync',
  async function (data: any) {
    const { sites, selectedDate, selectedTimeSet } = data;
    const customKpiMemberData = await buildMemberCustomRequest('economics', {
      sites,
      selectedDate,
      selectedTimeSet,
      includeLocaleOnGrouped: false,
    });

    // Fill missing timestamps for economics custom KPI data
    const filledCustomKpiMemberData = fillMissingTimestamps(
      customKpiMemberData,
      formatDate(selectedDate[0]),
      formatDate(selectedDate[1]),
      getTimeGranularityOverview(selectedTimeSet) as any
    );

    return filledCustomKpiMemberData;
  }
);

export const SetEnergyKpiDataCommunityRankingAsync = createAsyncThunk(
  'kpi/SetEnergyKpiDataCommunityRankingAsync',
  async function (data: any) {
    const { COMMUNITY_ID, selectedDate, selectedTimeSet } = data;

    const [energyRanking] = await Promise.all([
      httpService
        .post({
          url: `${ENV.VITE_APP_BASE_URL}/community/kpi/v2_0/siteCommunity/energy/ranking/${COMMUNITY_ID}`,
          data: {
            dateFrom: formatDate(selectedDate[0]),
            dateTo: formatDate(selectedDate[1]),
            timeGranularity: getTimeGranularityOverview(selectedTimeSet),
            locale: LOCALE,
            kpiLists: [
              [
                // 'consumption',
                'self_consumption_direct',
                'self_consumption_battery',
                'from_shared',
                'from_grid',
              ],
              [
                // 'production',
                'self_consumption_direct',
                'production_to_battery',
                'to_shared',
                'to_grid',
              ],
            ],
            rankingKpis: ['consumption', 'production'],
            limitTopN: 5,
          },
          // ${ENV.VITE_APP_BASE_URL}//community/kpi/v1_2/community/${metaDataApiStore.selectedEntityId}
        })
        .then((res: any) => {
          if (res.status === 200) {
            return res.data;
          } else {
            throw new Error(res.statusText);
            // console.warn({
            //   status: res.status,
            //   message: res.statusText,
            // });
            return null;
          }
        })
        .catch((err: any) => {
          // console.warn(err);
          throw new Error(err);

          // setAlertStore({
          //   type: AlertTypes.SetAlert,
          //   payload: {
          //     value: {
          //       type: 'BANNER_UNEXPECTED_ERROR'
          //     },
          //   }
          // })

          return null;
        }),
    ]);

    return {
      energyRanking: energyRanking || [],
    };
  }
);

export const SetEnergyKwickKpiDataCommunityRankingAsync = createAsyncThunk(
  'kpi/SetEnergyKwickKpiDataCommunityRankingAsync',
  async function (data: any) {
    const { COMMUNITY_ID, selectedDate, selectedTimeSet } = data;

    const [energyRanking] = await Promise.all([
      httpService
        .post({
          url: `${ENV.VITE_APP_BASE_URL}/community/kpi/v2_0/siteCommunity/energy/ranking/${COMMUNITY_ID}`,
          data: {
            dateFrom: formatDate(selectedDate[0]),
            dateTo: formatDate(selectedDate[1]),
            timeGranularity: getTimeGranularityOverview(selectedTimeSet),
            locale: LOCALE,
            kpiLists: [
              [
                // 'import_total',
                'from_shared',
                'from_grid',
              ],
              [
                // 'export_total',
                'to_shared',
                'to_grid',
              ],
            ],
            rankingKpis: ['import_total', 'export_total'],
            limitTopN: 5,
          },
          // ${ENV.VITE_APP_BASE_URL}//community/kpi/v1_2/community/${metaDataApiStore.selectedEntityId}
        })
        .then((res: any) => {
          if (res.status === 200) {
            return res.data;
          } else {
            throw new Error(res.statusText);
            // console.warn({
            //   status: res.status,
            //   message: res.statusText,
            // });
            return null;
          }
        })
        .catch((err: any) => {
          // console.warn(err);
          throw new Error(err);

          // setAlertStore({
          //   type: AlertTypes.SetAlert,
          //   payload: {
          //     value: {
          //       type: 'BANNER_UNEXPECTED_ERROR'
          //     },
          //   }
          // })

          return null;
        }),
    ]);

    return {
      energyRanking: energyRanking || [],
    };
  }
);

export const SetEconomicsKpiDataCommunityRankingAsync = createAsyncThunk(
  'kpi/SetEconomicsKpiDataCommunityRankingAsync',
  async function (data: any) {
    const { COMMUNITY_ID, selectedDate, selectedTimeSet } = data;

    const [economicsRanking, costsRanking] = await Promise.all([
      httpService
        .post({
          url: `${ENV.VITE_APP_BASE_URL}/community/kpi/v2_0/siteCommunity/economics/ranking/${COMMUNITY_ID}`,
          data: {
            dateFrom: formatDate(selectedDate[0]),
            dateTo: formatDate(selectedDate[1]),
            timeGranularity: getTimeGranularityOverview(selectedTimeSet),
            locale: LOCALE,
            rankingKpis: ['total_benefits'],
            kpiLists: [['total_benefits', 'revenues', 'savings']],
            limitTopN: 5,
          },
          // ${ENV.VITE_APP_BASE_URL}//community/kpi/v1_2/community/${metaDataApiStore.selectedEntityId}
        })
        .then((res: any) => {
          if (res.status === 200) {
            return res.data;
          } else {
            throw new Error(res.statusText);
            // console.warn({
            //   status: res.status,
            //   message: res.statusText,
            // });
            return null;
          }
        })
        .catch((err: any) => {
          // console.warn(err);
          throw new Error(err);

          // setAlertStore({
          //   type: AlertTypes.SetAlert,
          //   payload: {
          //     value: {
          //       type: 'BANNER_UNEXPECTED_ERROR'
          //     },
          //   }
          // })

          return null;
        }),

      httpService
        .post({
          url: `${ENV.VITE_APP_BASE_URL}/community/kpi/v2_0/siteCommunity/economics/ranking/${COMMUNITY_ID}`,
          data: {
            dateFrom: formatDate(selectedDate[0]),
            dateTo: formatDate(selectedDate[1]),
            timeGranularity: getTimeGranularityOverview(selectedTimeSet),
            locale: LOCALE,
            rankingKpis: ['total_costs'],
            kpiLists: [['total_costs']],
            limitTopN: 5,
          },
          // ${ENV.VITE_APP_BASE_URL}//community/kpi/v1_2/community/${metaDataApiStore.selectedEntityId}
        })
        .then((res: any) => {
          if (res.status === 200) {
            return res.data;
          } else {
            throw new Error(res.statusText);
            // console.warn({
            //   status: res.status,
            //   message: res.statusText,
            // });
            return null;
          }
        })
        .catch((err: any) => {
          // console.warn(err);
          throw new Error(err);

          // setAlertStore({
          //   type: AlertTypes.SetAlert,
          //   payload: {
          //     value: {
          //       type: 'BANNER_UNEXPECTED_ERROR'
          //     },
          //   }
          // })

          return null;
        }),
    ]);

    return {
      economicsRanking: economicsRanking || [],
      costsRanking: costsRanking || [],
    };
  }
);

export const SetEconomicsKwickKpiDataCommunityRankingAsync = createAsyncThunk(
  'kpi/SetEconomicsKwickKpiDataCommunityRankingAsync',
  async function (data: any) {
    const { COMMUNITY_ID, selectedDate, selectedTimeSet } = data;

    const [economicsRanking, costsRanking] = await Promise.all([
      httpService
        .post({
          url: `${ENV.VITE_APP_BASE_URL}/community/kpi/v2_0/siteCommunity/economics/ranking/${COMMUNITY_ID}`,
          data: {
            dateFrom: formatDate(selectedDate[0]),
            dateTo: formatDate(selectedDate[1]),
            timeGranularity: getTimeGranularityOverview(selectedTimeSet),
            locale: LOCALE,
            rankingKpis: ['total_costs'],
            kpiLists: [['total_costs', 'community_costs', 'other_costs']],
            limitTopN: 5,
          },
          // ${ENV.VITE_APP_BASE_URL}//community/kpi/v1_2/community/${metaDataApiStore.selectedEntityId}
        })
        .then((res: any) => {
          if (res.status === 200) {
            return res.data;
          } else {
            throw new Error(res.statusText);
            // console.warn({
            //   status: res.status,
            //   message: res.statusText,
            // });
            return null;
          }
        })
        .catch((err: any) => {
          // console.warn(err);
          throw new Error(err);

          // setAlertStore({
          //   type: AlertTypes.SetAlert,
          //   payload: {
          //     value: {
          //       type: 'BANNER_UNEXPECTED_ERROR'
          //     },
          //   }
          // })

          return null;
        }),

      httpService
        .post({
          url: `${ENV.VITE_APP_BASE_URL}/community/kpi/v2_0/siteCommunity/economics/ranking/${COMMUNITY_ID}`,
          data: {
            dateFrom: formatDate(selectedDate[0]),
            dateTo: formatDate(selectedDate[1]),
            timeGranularity: getTimeGranularityOverview(selectedTimeSet),
            locale: LOCALE,
            rankingKpis: ['total_costs'],
            kpiLists: [['total_costs']],
            limitTopN: 5,
          },
          // ${ENV.VITE_APP_BASE_URL}//community/kpi/v1_2/community/${metaDataApiStore.selectedEntityId}
        })
        .then((res: any) => {
          if (res.status === 200) {
            return res.data;
          } else {
            throw new Error(res.statusText);
            // console.warn({
            //   status: res.status,
            //   message: res.statusText,
            // });
            return null;
          }
        })
        .catch((err: any) => {
          // console.warn(err);
          throw new Error(err);

          // setAlertStore({
          //   type: AlertTypes.SetAlert,
          //   payload: {
          //     value: {
          //       type: 'BANNER_UNEXPECTED_ERROR'
          //     },
          //   }
          // })

          return null;
        }),
    ]);

    return {
      economicsRanking: economicsRanking || [],
      costsRanking: costsRanking || [],
    };
  }
);

export const SetEnergyKpiDataMemberRankingAsync = createAsyncThunk(
  'kpi/SetEnergyKpiDataMemberRankingAsync',
  async function (data: any) {
    const { MEMBER_ID, selectedDate, selectedTimeSet } = data;

    const [energyRanking] = await Promise.all([
      httpService
        .post({
          url: `${ENV.VITE_APP_BASE_URL}/community/kpi/v2_0/siteMember/energy/ranking/${MEMBER_ID}`,
          data: {
            dateFrom: formatDate(selectedDate[0]),
            dateTo: formatDate(selectedDate[1]),
            timeGranularity: getTimeGranularityOverview(selectedTimeSet),
            locale: LOCALE,
            kpiLists: [
              [
                // 'consumption',
                'self_consumption_direct',
                'self_consumption_battery',
                'from_shared',
                'from_grid',
              ],
              [
                // 'production',
                'self_consumption_direct',
                'production_to_battery',
                'to_shared',
                'to_grid',
              ],
            ],
            rankingKpis: ['consumption', 'production'],
            limitTopN: 5,
          },
          // ${ENV.VITE_APP_BASE_URL}//community/kpi/v1_2/community/${metaDataApiStore.selectedEntityId}
        })
        .then((res: any) => {
          if (res.status === 200) {
            return res.data;
          } else {
            // console.warn({
            //   status: res.status,
            //   message: res.statusText,
            // });
            return null;
          }
        })
        .catch((err: any) => {
          // console.warn(err);

          // setAlertStore({
          //   type: AlertTypes.SetAlert,
          //   payload: {
          //     value: {
          //       type: 'BANNER_UNEXPECTED_ERROR'
          //     },
          //   }
          // })

          return null;
        }),
    ]);

    return {
      energyRanking: energyRanking || [],
    };
  }
);
export const SetEnergyKwickKpiDataMemberRankingAsync = createAsyncThunk(
  'kpi/SetEnergyKwickKpiDataMemberRankingAsync',
  async function (data: any) {
    const { MEMBER_ID, selectedDate, selectedTimeSet } = data;

    const [energyRanking] = await Promise.all([
      httpService
        .post({
          url: `${ENV.VITE_APP_BASE_URL}/community/kpi/v2_0/siteMember/energy/ranking/${MEMBER_ID}`,
          data: {
            dateFrom: formatDate(selectedDate[0]),
            dateTo: formatDate(selectedDate[1]),
            timeGranularity: getTimeGranularityOverview(selectedTimeSet),
            locale: LOCALE,
            kpiLists: [
              [
                // 'import_total',
                'from_shared',
                'from_grid',
              ],
              [
                // 'export_total',
                'to_shared',
                'to_grid',
              ],
            ],
            rankingKpis: ['import_total', 'export_total'],
            limitTopN: 5,
          },
          // ${ENV.VITE_APP_BASE_URL}//community/kpi/v1_2/community/${metaDataApiStore.selectedEntityId}
        })
        .then((res: any) => {
          if (res.status === 200) {
            return res.data;
          } else {
            // console.warn({
            //   status: res.status,
            //   message: res.statusText,
            // });
            return null;
          }
        })
        .catch((err: any) => {
          // console.warn(err);

          // setAlertStore({
          //   type: AlertTypes.SetAlert,
          //   payload: {
          //     value: {
          //       type: 'BANNER_UNEXPECTED_ERROR'
          //     },
          //   }
          // })

          return null;
        }),
    ]);

    return {
      energyRanking: energyRanking || [],
    };
  }
);

export const SetEconomicsKpiDataMemberRankingAsync = createAsyncThunk(
  'kpi/SetEconomicsKpiDataMemberRankingAsync',
  async function (data: any) {
    const { MEMBER_ID, selectedDate, selectedTimeSet } = data;

    const [economicsRanking] = await Promise.all([
      httpService
        .post({
          url: `${ENV.VITE_APP_BASE_URL}/community/kpi/v2_0/siteMember/economics/ranking/${MEMBER_ID}`,
          data: {
            dateFrom: formatDate(selectedDate[0]),
            dateTo: formatDate(selectedDate[1]),
            timeGranularity: getTimeGranularityOverview(selectedTimeSet),
            locale: LOCALE,
            rankingKpis: ['total_benefits'],
            kpiLists: [
              [
                // 'total_benefits',
                'revenues',
                'savings',
              ],
            ],
            limitTopN: 5,
          },
          // ${ENV.VITE_APP_BASE_URL}//community/kpi/v1_2/community/${metaDataApiStore.selectedEntityId}
        })
        .then((res: any) => {
          if (res.status === 200) {
            return res.data;
          } else {
            // console.warn({
            //   status: res.status,
            //   message: res.statusText,
            // });
            return null;
          }
        })
        .catch((err: any) => {
          // console.warn(err);

          // setAlertStore({
          //   type: AlertTypes.SetAlert,
          //   payload: {
          //     value: {
          //       type: 'BANNER_UNEXPECTED_ERROR'
          //     },
          //   }
          // })

          return null;
        }),
    ]);

    return {
      economicsRanking: economicsRanking || [],
    };
  }
);

export const SetEconomicsKwickKpiDataMemberRankingAsync = createAsyncThunk(
  'kpi/SetEconomicsKwickKpiDataMemberRankingAsync',
  async function (data: any) {
    const { MEMBER_ID, selectedDate, selectedTimeSet } = data;

    const [economicsRanking] = await Promise.all([
      httpService
        .post({
          url: `${ENV.VITE_APP_BASE_URL}/community/kpi/v2_0/siteMember/economics/ranking/${MEMBER_ID}`,
          data: {
            dateFrom: formatDate(selectedDate[0]),
            dateTo: formatDate(selectedDate[1]),
            timeGranularity: getTimeGranularityOverview(selectedTimeSet),
            locale: LOCALE,
            rankingKpis: ['total_costs'],
            kpiLists: [
              [
                // 'total_costs',
                'community_costs',
                'other_costs',
              ],
            ],
            limitTopN: 5,
          },
          // ${ENV.VITE_APP_BASE_URL}//community/kpi/v1_2/community/${metaDataApiStore.selectedEntityId}
        })
        .then((res: any) => {
          if (res.status === 200) {
            return res.data;
          } else {
            // console.warn({
            //   status: res.status,
            //   message: res.statusText,
            // });
            return null;
          }
        })
        .catch((err: any) => {
          // console.warn(err);

          // setAlertStore({
          //   type: AlertTypes.SetAlert,
          //   payload: {
          //     value: {
          //       type: 'BANNER_UNEXPECTED_ERROR'
          //     },
          //   }
          // })

          return null;
        }),
    ]);

    return {
      economicsRanking: economicsRanking || [],
    };
  }
);

// --------- KWICK INVOICES ---------
export const FetchKwickInvoicesAsync = createAsyncThunk(
  'kpi/FetchKwickInvoicesAsync',
  async function (data: {
    siteIds: string[];
    communityFilter?: { communityId: string; sites: any[] };
    memberFilter?: { memberId: string; sites: any[] };
  }) {
    // Token auto-injected by httpService interceptor
    // NOTE: getInvoicesOverview removed - frontend calculates aggregations from invoices data
    // CM-5128: a transport failure or non-200 must REJECT the thunk (status
    // 'ERROR'), not fulfil with null — otherwise a denied/failed fetch is
    // indistinguishable from "this member has no invoices" in every consumer.
    const rawResponse = await httpService
      .post({
        url: '/custom/kwick/v1/getInvoices',
        data: { site_ids: (data?.siteIds || []).filter((s) => s != null) },
      })
      .then((res: any) => {
        if (res.status !== 200) {
          throw new Error(`getInvoices failed with status ${res.status}`);
        }
        return res.data;
      });

    // The backend returns a bare array when invoices exist, but an object
    // `{ message, invoices: [] }` when a site has none. Normalise to an array
    // (success) or `null` (no invoices). `null` makes the invoices view show
    // its existing empty state instead of `.map()`-ing the object shape and
    // blanking the whole screen.
    const rawInvoices = normalizeInvoicesResponse(rawResponse);

    // Filter invoices by community period if a communityFilter is provided
    let invoices = rawInvoices;
    if (data.communityFilter && rawInvoices && Array.isArray(rawInvoices)) {
      invoices = filterInvoicesByCommunityPeriod(
        rawInvoices,
        data.communityFilter.communityId,
        data.communityFilter.sites
      );
    }

    // Filter invoices by member ownership period if a memberFilter is provided
    if (data.memberFilter && invoices && Array.isArray(invoices)) {
      invoices = filterInvoicesByMemberPeriod(
        invoices,
        data.memberFilter.memberId,
        data.memberFilter.sites
      );
    }

    // Calculate aggregations by period (replaces removed getInvoicesOverview endpoint)
    let overview: any[] = [];
    if (invoices && Array.isArray(invoices)) {
      const groupedByPeriod: Record<string, any[]> = {};

      // Group invoices by the actual invoice date range. invoice_period is a
      // backend/internal label and can be misleading for split periods.
      invoices.forEach((invoice: any) => {
        const startDate = invoice.period_start_date || '';
        const endDate = invoice.period_end_date || '';
        const period =
          startDate || endDate
            ? `${startDate}|${endDate}`
            : invoice.invoice_period || 'unknown';
        if (!groupedByPeriod[period]) {
          groupedByPeriod[period] = [];
        }
        groupedByPeriod[period].push(invoice);
      });

      // Calculate aggregates for each period
      overview = Object.values(groupedByPeriod).map((periodInvoices) => {
        const amount = periodInvoices.reduce(
          (sum, inv) => sum + (inv.amount || 0),
          0
        );
        const received = periodInvoices.reduce(
          (sum, inv) => sum + (inv.is_paid ? inv.amount || 0 : 0),
          0
        );

        return {
          invoice_period: periodInvoices[0]?.invoice_period,
          period_start_date: periodInvoices[0]?.period_start_date,
          period_end_date: periodInvoices[0]?.period_end_date,
          amount,
          received,
        };
      });
    }

    return { invoices, overview, siteIds: data?.siteIds || [] } as any;
  }
);

export const FetchKwickInvoiceFileAsync = createAsyncThunk(
  'kpi/FetchKwickInvoiceFileAsync',
  async function (data: { invoiceNo: string; siteId: string }) {
    // Token auto-injected by httpService interceptor
    const response = await httpService
      .get({
        url: `/custom/kwick/v1/getInvoiceFile/${encodeURIComponent(data.invoiceNo)}`,
        params: { site_id: data.siteId },
      })
      .then((res: any) => (res.status === 200 ? res.data : null))
      .catch(() => null);

    return response;
  }
);
